import {reactive}          from 'vue';
import ContactPersonItem   from './ContactPersonItem';
import ContactGroupItem    from './ContactGroupItem';
import ContactItem         from './ContactItem';
import ContactsCollection  from '@/js/Contacts/Collections/ContactsCollection';

export {ContactsData as default};

/**
* Contacts Data Entity
*
* Defines Contacts data struture
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsData {
  /**
   * All Contacts
   *
   * @public
   *
   * @type {ContactsCollection}
   *
   * @since 7.0.0
   */
  all = new ContactsCollection();

  /**
   * Contacts Groups
   *
   * @public
   *
   * @type {ContactsCollection}
   *
   * @since 7.0.0
   */
  groups = new ContactsCollection();

  /**
   * Contacts persons
   *
   * @public
   *
   * @type {ContactsCollection}
   *
   * @since 7.0.0
   */
  persons = new ContactsCollection();

  /**
   * Frequent contacts
   *
   * This is a special list
   * and for now does not have any influence over the other lists
   *
   * @public
   *
   * @type {ContactsCollection}
   *
   * @since 7.0.0
   */
  frequent = new ContactsCollection();

  /**
   * Defines if contacts were fetched
   *
   * This is a special list
   * and for now does not have any influence over the other lists
   *
   * @public
   *
   * @type {Boolean}
   *
   * @since 7.0.0
   */
  fetched = false;

  /**
   * Creates an empty contact item by type
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} type contact type
   *
   * @return {ContactGroupItem|ContactGroupGroup|ContactItem}
   */
  createItem(type) {
    if (type === 'group') {
      return new ContactGroupItem();
    }

    if (type === 'person') {
      return new ContactPersonItem();
    }

    return new ContactItem()
  }

  /**
   * Adds a contact to tthe corresponding list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactItem} contact
   *
   * @return {void}
   */
  addItem(contact) {
    this.all.add(contact);

    if (contact.type === 'group') {
      this.groups.add(contact);
      return;
    }

    if (contact.type === 'person') {
      this.persons.add(contact);
      return;
    }
  }

  /**
   * Returns a contact item from the index,
   * or an empty ContactItem if index doesn't exist
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} id contact id
   *
   * @return {ContactItem}
   */
  getItem(id) {
    return (!this.all.index[id]) ? new ContactItem() : this.all.index[id];
  }

  /**
   * Returns a clone of a person by id
   *
   * This method will not return the origin object, but instead a clone of the object with all the original data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} person_id person id
   *
   * @return {ContactPersonItem}
   */
  getPersonClone(person_id) {
    let clone = new ContactPersonItem();

    return (this.persons.exists(person_id))
      ? Object.assign(clone, this.persons.get(person_id))
      : clone;
  }

  /**
   * Returns a clone of a group by id
   *
   * This method will not return the origin object, but instead a clone of the object with all the original data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} group_id group id
   *
   * @return {ContactGroupItem}
   */
  getGroupClone(group_id) {
    let clone = new ContactGroupItem();

    return (this.groups.exists(group_id))
      ? Object.assign(clone, this.groups.get(group_id))
      : clone;
  }

  /**
   * Returns a contact collection list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} name list name
   *
   * @return {ContactsCollection}
   */
  getList(name) {
    return (!this[name]) ? this.all : this[name];
  }

  /**
   * Deletes a contact from all indexed lists
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} contact_id person id to be deleted
   *
   * @return {void}
   */
  deleteItem(contact_id) {
    this.groups.delete(contact_id)
    this.persons.delete(contact_id);
    this.all.delete(contact_id);
  }

  /**
   * Resets all contact lists
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.groups.reset()
    this.persons.reset();
    this.all.reset();
  }

  /**
   * Resorts All items in the list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resort() {
    this.all.resortItems();
    this.persons.resortItems();
    this.groups.resortItems();
  }

  /**
   * Searchs contacts index to find a contact with the given email
   *
   * If a person is not found creates a new entity with the input data assigned to the email field
   *
   * Adds the first exact match found to the frequent collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} email search input
   *
   * @return {void}
   */
  addFrequentByEmail(email) {
    email      = email.toLowerCase();
    let person = this.persons.items.find((contact) => {
      return (contact.email && contact.email.toLowerCase() === email) ? true : false
    });

    if (!person) {
      person           = new ContactPersonItem();
      person.id        = email;
      person.name      = email,
      person.email     = email,
      person.recipient = email;
      person.temporary = true;
    }

    this.frequent.add(person);
  }

  /**
   * Adds all persons to the group members list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  addMembersToGroups() {
    this.persons.items.forEach((person) => {
      person.groups = [];
    })

    this.groups.items.forEach((group) => {
      group.members_count = 0;
      group.members       = {};
      group.members_ids.forEach((id) => {
        if (!this.all.index[id]) {
          return;
        }

        group.members_count++;
        group.members[id] = this.all.index[id];
        this.all.index[id].groups.push(group);
      })
    })
  }
}